import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import {
    AdditionalRuleModel,
    DrainageVentilationRuleModel,
    GlazingRuleModel,
    IntegratedRuleModel,
    JointRuleModel,
    LimitationRuleModel,
    MatingRuleModel,
    RuleModel,
    DrainageVentilationItemModel,
    DrainageVentilationSectionModel,
    GlazingItemModel,
    IntegratedItemModel,
    LimitationItemModel,
    JointSectionModel,
    CrossSectionModel,
    SystemModel,
    QueryParams,
    SystemModelPagedResponseModel,
    TransformationModel
} from '../../../api/models';
import { AppToastService } from '../../toast-service/toast-service';
import { SystemsService } from 'src/app/api/services/systems.service';

type RuleType = 'additional' | 'drainage-ventilation' | 'glazing' | 'integrated' | 'joint' | 'limitation' | 'mating';
type RuleModelType = AdditionalRuleModel | DrainageVentilationRuleModel | GlazingRuleModel |
    IntegratedRuleModel | JointRuleModel | LimitationRuleModel | MatingRuleModel;

// Configuration for each rule type
interface RuleTypeConfig {
    hasRules: boolean;
    hasItems: boolean;
    hasJoints: boolean;
    hasSections: boolean;
    displayName: string;
}

export interface RuleFormOutput {
  rule: RuleModelType;
  system: SystemModel;    // or your actual SystemModel type
}

const RULE_TYPE_CONFIG: Record<RuleType, RuleTypeConfig> = {
    'additional': { hasRules: true, hasItems: false, hasJoints: false, hasSections: false, displayName: 'Additional Rules' },
    'drainage-ventilation': { hasRules: false, hasItems: true, hasJoints: false, hasSections: false, displayName: 'Drainage Ventilation Rules' },
    'glazing': { hasRules: false, hasItems: true, hasJoints: false, hasSections: false, displayName: 'Glazing Rules' },
    'integrated': { hasRules: false, hasItems: true, hasJoints: false, hasSections: false, displayName: 'Integrated Rules' },
    'joint': { hasRules: false, hasItems: false, hasJoints: true, hasSections: false, displayName: 'Joint Rules' },
    'limitation': { hasRules: false, hasItems: true, hasJoints: false, hasSections: false, displayName: 'Limitation Rules' },
    'mating': { hasRules: false, hasItems: false, hasJoints: false, hasSections: true, displayName: 'Mating Rules' }
};

@Component({
    selector: 'app-rule-form',
    templateUrl: './rule-form.component.html',
    styleUrls: ['./rule-form.component.scss'],
    standalone: false
})
export class RuleFormComponent implements OnInit, OnChanges {
    @Input() ruleType!: RuleType;
    @Input() ruleData: RuleModelType | null = null;
    @Input() existingRules: RuleModelType[] = [];
    @Input() isEditMode = false;
    @Input() selectedSystemId: string | null = null;
    //@Output() save = new EventEmitter<RuleModelType>();
    @Output() save = new EventEmitter<RuleFormOutput>();
    @Output() cancel = new EventEmitter<void>();
    @ViewChild('matingRuleEditComponent') matingRuleEditComponent: any;

    ruleForm!: FormGroup;
    loading = false;
    errorMessage: string = '';
    systems: SystemModel[] = [];
    selectedSystem: SystemModel | null = null;

    constructor(private fb: FormBuilder, private toastService: AppToastService, private systemsService: SystemsService) { }

    ngOnInit(): void {
        this.toastService.clear();
        this.initializeForm();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['ruleData'] && this.ruleForm) {
            console.log('rule data', this.ruleData);
            this.populateForm();
        }

        if (changes['selectedSystemId'] && this.ruleForm) {
        this.applySelectedSystem();
    }
    }

    private applySelectedSystem(): void {
    if (!this.ruleForm) return;

    if (this.selectedSystemId) {
        // Auto-select system
        this.ruleForm.patchValue({
            systemId: this.selectedSystemId
        });

        
    } else {
        // Enable dropdown when no system is preselected
        this.ruleForm.patchValue({
            systemId: ''
        });
    }
}

    private initializeForm(): void {
        const config = RULE_TYPE_CONFIG[this.ruleType];
        
        this.ruleForm = this.fb.group({
            id: [ ''],
            ruleId: ['', Validators.required],
            name: ['', Validators.required],
            systemId: ['', Validators.required],
            description: [''],
            rules: this.fb.array([]),
            items: this.fb.array([]),
            joints: this.fb.array([]),
            sections: this.fb.array([]),
            jointSections: this.fb.array([]),
            crossSections: this.fb.array([]),
            createdAt: [''],
            updatedAt: ['']
        });

        // Add initial items based on rule type
        if (!this.ruleData) {
            this.addInitialItems();
        }
        
        this.populateForm();
    }

    private addInitialItems(): void {
        const config = RULE_TYPE_CONFIG[this.ruleType];
        
        if (config.hasRules) {
            this.addRule();
        } else if (config.hasItems) {
            this.addItemByType();
        } else if (config.hasJoints) {
            this.addJoint();
        } else if (config.hasSections) {
            this.addSection();
        }
    }

    private addItemByType(): void {
        switch (this.ruleType) {
            case 'drainage-ventilation':
                this.addDrainageVentilationItem();
                break;
            case 'glazing':
                this.addGlazingItem();
                break;
            case 'integrated':
                this.addIntegratedItem();
                break;
            case 'limitation':
                this.addLimitationItem();
                break;
        }
    }

    private populateForm(): void {
        this.getSystems();
        if (!this.ruleData) return;

        // Clear existing form arrays
        this.clearFormArrays();

        // Set basic properties for joint and mating types
        if (this.ruleType === 'joint') {
            const jointRule = this.ruleData as JointRuleModel;
            this.ruleForm.patchValue({
                id: jointRule.id,
                ruleId: jointRule.ruleId,
                name: jointRule.name,
                description: jointRule.description,
                createdAt: jointRule.createdAt,
                updatedAt: jointRule.updatedAt
            });
            
            if (jointRule.jointSections) {
                jointRule.jointSections.forEach(section => this.addJointSection(null, section));
            }
        } else if (this.ruleType === 'mating') {
            const matingRule = this.ruleData as MatingRuleModel;
            this.ruleForm.patchValue({
                id: matingRule.id,
                ruleId: matingRule.ruleId,
                name: matingRule.name,
                description: matingRule.description,
                createdAt: matingRule.createdAt,
                updatedAt: matingRule.updatedAt
            });
            
            if (matingRule.crossSections) {
                matingRule.crossSections.forEach(cs => this.addCrossSection(null, cs));
            }
        } else {
            // Handle rules-based types (additional, drainage-ventilation, glazing, integrated, limitation)
            this.ruleForm.patchValue({
                id: this.ruleData.id,
                createdAt: this.ruleData.createdAt,
                updatedAt: this.ruleData.updatedAt
            });

            // Populate rules if present
            if ('rules' in this.ruleData && this.ruleData.rules) {
                this.ruleData.rules.forEach(rule => this.addRule(rule));
            }

            // Populate items based on type
            if ('items' in this.ruleData && this.ruleData.items) {
                this.ruleData.items.forEach(item => {
                    switch (this.ruleType) {
                        case 'drainage-ventilation':
                            this.addDrainageVentilationItem(item as DrainageVentilationItemModel);
                            break;
                        case 'glazing':
                            this.addGlazingItem(item as GlazingItemModel);
                            break;
                        case 'integrated':
                            this.addIntegratedItem(item as IntegratedItemModel);
                            break;
                        case 'limitation':
                            this.addLimitationItem(item as LimitationItemModel);
                            break;
                    }
                });
            }
        }
    }

    private clearFormArrays(): void {
        ['rules', 'items', 'joints', 'sections', 'jointSections', 'crossSections'].forEach(arrayName => {
            const formArray = this.ruleForm.get(arrayName) as FormArray;
            if (formArray) {
                while (formArray.length !== 0) {
                    formArray.removeAt(0);
                }
            }
        });
    }

    // Rule management
    get rulesArray(): FormArray {
        return this.ruleForm.get('rules') as FormArray;
    }

    addRule(rule?: RuleModel): void {
        const ruleGroup = this.fb.group({
            id: [rule?.id || ''],
            ruleId: [rule?.ruleId || '', Validators.required],
            description: [rule?.description || '', Validators.required],
            customCode: [rule?.customCode || '']
        });
        this.rulesArray.push(ruleGroup);
    }

    removeRule(index: number): void {
        this.rulesArray.removeAt(index);
    }

    // Items management (for drainage-ventilation, glazing, integrated, limitation)
    get itemsArray(): FormArray {
        return this.ruleForm.get('items') as FormArray;
    }

    addDrainageVentilationItem(item?: DrainageVentilationItemModel): void {
        const itemGroup = this.fb.group({
            id: [item?.id || ''],
            dvId: [item?.dvId || '', Validators.required],
            description: [item?.description || '', Validators.required],
            imageUrl: [item?.imageUrl || ''],
            drainageVentilationSections: this.fb.array([])
        });

        // Add sections if provided
        if (item?.drainageVentilationSections) {
            item.drainageVentilationSections.forEach((section: any) => {
                this.addDrainageVentilationSection(itemGroup, section);
            });
        } else {
            // Add one empty section by default
            this.addDrainageVentilationSection(itemGroup);
        }

        this.itemsArray.push(itemGroup);
    }

    removeDrainageVentilationItem(index: number): void {
        this.itemsArray.removeAt(index);
    }

    addDrainageVentilationSection(itemGroup: any, section?: DrainageVentilationSectionModel): void {
        const sectionsArray = itemGroup.get('drainageVentilationSections') as FormArray;
        const sectionGroup = this.fb.group({
            id: [section?.id || ''],
            description: [section?.description || '', Validators.required],
            type: [section?.type || '', Validators.required],
            articles: this.fb.array([])
        });
        sectionsArray.push(sectionGroup);
    }

    removeDrainageVentilationSection(itemIndex: number, sectionIndex: number): void {
        const itemGroup = this.itemsArray.at(itemIndex) as FormGroup;
        const sectionsArray = itemGroup.get('drainageVentilationSections') as FormArray;
        sectionsArray.removeAt(sectionIndex);
    }

    // Glazing Items
    addGlazingItem(item?: GlazingItemModel): void {
        const itemGroup = this.fb.group({
            id: [item?.id || ''],
            ruleId: [item?.ruleId || '', Validators.required],
            template: [item?.template || '', Validators.required],
            description: [item?.description || '', Validators.required],
            decisionTable: [item?.decisionTable || []]
        });
        this.itemsArray.push(itemGroup);
    }

    removeGlazingItem(index: number): void {
        this.itemsArray.removeAt(index);
    }

    // Integrated Items
    addIntegratedItem(item?: IntegratedItemModel): void {
        const itemGroup = this.fb.group({
            id: [item?.id || ''],
            name: [item?.name || '', Validators.required],
            type: [item?.type || '', Validators.required],
            description: [item?.description || '', Validators.required],
            imageUrl: [item?.imageUrl || '']
        });
        this.itemsArray.push(itemGroup);
    }

    removeIntegratedItem(index: number): void {
        this.itemsArray.removeAt(index);
    }

    // Joints (for joint rules)
    get jointsArray(): FormArray {
        return this.ruleForm.get('joints') as FormArray;
    }

    get jointSectionsArray(): FormArray {
        return this.ruleForm.get('jointSections') as FormArray;
    }

    addJoint(joint?: JointRuleModel): void {
        // For joint rules, we manage sections directly at form root level
        if (joint) {
            this.ruleForm.patchValue({
                id: joint.id,
                ruleId: joint.ruleId,
                name: joint.name,
                description: joint.description
            });
            
            if (joint.jointSections) {
                joint.jointSections.forEach(section => this.addJointSection(null, section));
            }
        }
    }

    removeJoint(index: number): void {
        this.jointsArray.removeAt(index);
    }

    addJointSection(jointGroup: any, section?: JointSectionModel): void {
        const sectionsArray = this.jointSectionsArray;
        const sectionGroup = this.fb.group({
            id: [section?.id || ''],
            description: [section?.description || '', Validators.required],
            material: [section?.material || '', Validators.required],
            type: [section?.type || '', Validators.required],
            articles: [section?.articles || []],
            imageUrl: [section?.imageUrl || '']
        });
        sectionsArray.push(sectionGroup);
    }

    removeJointSection(jointIndex: number, sectionIndex: number): void {
        this.jointSectionsArray.removeAt(sectionIndex);
    }

    // Limitation Items
    addLimitationItem(item?: LimitationItemModel): void {
        const itemGroup = this.fb.group({
            id: [item?.id || ''],
            name: [item?.name || '', Validators.required],
            type: [item?.type || '', Validators.required],
            imageUrl: [item?.imageUrl || ''],
            dimensions: [item?.dimensions || []]
        });
        this.itemsArray.push(itemGroup);
    }

    removeLimitationItem(index: number): void {
        this.itemsArray.removeAt(index);
    }

    // Sections and Cross Sections (for mating rules)
    get sectionsArray(): FormArray {
        return this.ruleForm.get('sections') as FormArray;
    }

    get crossSectionsArray(): FormArray {
        return this.ruleForm.get('crossSections') as FormArray;
    }

    addSection(section?: MatingRuleModel): void {
        // For mating rules, we manage cross sections directly at form root level
        if (section) {
            this.ruleForm.patchValue({
                id: section.id,
                ruleId: section.ruleId,
                name: section.name,
                description: section.description
            });
            
            if (section.crossSections) {
                section.crossSections.forEach(cs => this.addCrossSection(null, cs));
            }
        }
    }

    removeSection(index: number): void {
        this.sectionsArray.removeAt(index);
    }

    addCrossSection(sectionGroup: any, crossSection?: CrossSectionModel): void {
        const crossSectionsArray = this.crossSectionsArray;
        const crossSectionGroup = this.fb.group({
            id: [crossSection?.id || ''],
            description: [crossSection?.description || '', Validators.required],
            material: [crossSection?.material || '', Validators.required],
            type: [crossSection?.type || '', Validators.required],
            articles: [crossSection?.articles || []],
            imageUrl: [crossSection?.imageUrl || ''],
            positionX: [crossSection?.transformation?.positionX || 0, Validators.required],
            positionY: [crossSection?.transformation?.positionY || 0, Validators.required],
            positionZ: [crossSection?.transformation?.positionZ || 0, Validators.required],
            scaleX: [crossSection?.transformation?.scaleX || 1, Validators.required],
            scaleY: [crossSection?.transformation?.scaleY || 1, Validators.required],
            rotation: [crossSection?.transformation?.rotation || 0, Validators.required],
            isMirrored: [crossSection?.transformation?.isMirrored || false]
        });
        crossSectionsArray.push(crossSectionGroup);
    }

    removeCrossSection(sectionIndex: number, crossSectionIndex: number): void {
        this.crossSectionsArray.removeAt(crossSectionIndex);
    }

    onSubmit(): void {
        if (!this.ruleForm.valid) {
            this.markFormGroupTouched();
            return;
        }

        this.loading = true;
        const formValue = this.ruleForm.value;
        console.log('Form Value on Submit:', formValue);
        console.log('systemId on Submit:', formValue.systemId);

        this.selectedSystem = this.systems.find(s => s.id === formValue.systemId) || null;
        console.log('Selected System on Submit:', this.selectedSystem);

        // Build the appropriate model based on rule type
        let ruleModel: RuleModelType;

        if (this.ruleType === 'joint') {
            const jointModel = this.buildJointRuleModel(formValue);

            // Validate unique ruleId
            if (this.isDuplicateRuleId(jointModel.ruleId, jointModel.id)) {
                this.showError("Rule ID must be unique.");
                return;
            }
            ruleModel = jointModel;
        } else if (this.ruleType === 'mating') {
            const matingModel = this.buildMatingRuleModel(formValue);

            // Validate unique ruleId
            if (this.isDuplicateRuleId(matingModel.ruleId, matingModel.id)) {
                this.showError("Rule ID must be unique.");
                return;
            }
            // matingModel.crossSections?.forEach(element => {
            //     element.articleIds = this.selectedSystem?.articleIds || [];
            // });
            ruleModel = matingModel;
        } else {
            // Handle other rule types (additional, drainage-ventilation, glazing, integrated, limitation)
            ruleModel = this.cleanFormData(formValue) as RuleModelType;
        }

        //this.save.emit(ruleModel);
        this.save.emit({
            rule: ruleModel,
            system: this.selectedSystem ?? {} as SystemModel   // IMPORTANT
        });
        this.loading = false;
    }

    private buildJointRuleModel(formValue: any): JointRuleModel {
        return {
            id: formValue.id,
            ruleId: formValue.ruleId,
            name: formValue.name,
            description: formValue.description,
            systemId: formValue.systemId,
            jointSections: (formValue.jointSections || []).map((section: any) => ({
                id: section.id,
                description: section.description,
                material: section.material,
                type: section.type,
                articles: section.articles || [],
                imageUrl: section.imageUrl
            })),
            createdAt: formValue.createdAt,
            updatedAt: formValue.updatedAt
        };
    }

    private buildMatingRuleModel(formValue: any): MatingRuleModel {
        return {
            id: formValue.id,
            ruleId: formValue.ruleId,
            name: formValue.name,
            description: formValue.description,
            crossSections: (formValue.crossSections || []).map((cs: any) => ({
                id: cs.id,
                description: cs.description,
                material: cs.material,
                type: cs.type,
                articles: cs.articles || [],
                articleIds: cs.articleIds || [],
                imageUrl: cs.imageUrl,
                transformation: {
                    positionX: cs.positionX || 0,
                    positionY: cs.positionY || 0,
                    positionZ: cs.positionZ || 0,
                    rotation: cs.rotation || 0,
                    scaleX: cs.scaleX || 1,
                    scaleY: cs.scaleY || 1,
                    isMirrored: cs.isMirrored || false
                }
            })),
            createdAt: formValue.createdAt,
            updatedAt: formValue.updatedAt
        };
    }

    private isDuplicateRuleId(ruleId: string | undefined, currentId: string | undefined): boolean {
        if (!ruleId) return false;
        
        return this.existingRules.some((r: any) => {
            const existingRuleId = r.ruleId || r.sectionId;
            const existingId = r.id;
            return existingRuleId === ruleId && existingId !== currentId;
        });
    }

    private showError(message: string): void {
        this.errorMessage = message;
         this.toastService.clear();
        this.toastService.show(this.errorMessage, { classname: 'bg-danger text-light', delay: 2000 });
        setTimeout(() => {
            this.cancel.emit();
        }, 2500);
        this.loading = false;
    }

    onCancel(): void {
        this.cancel.emit();
    }

    private cleanFormData(data: any): any {
        const cleaned = { ...data };

        // Remove empty arrays
        ['rules', 'items', 'joints', 'sections', 'jointSections', 'crossSections'].forEach(key => {
            if (cleaned[key] && Array.isArray(cleaned[key]) && cleaned[key].length === 0) {
                delete cleaned[key];
            }
        });

        // Remove empty string values
        Object.keys(cleaned).forEach(key => {
            if (cleaned[key] === '' || cleaned[key] === null || cleaned[key] === undefined) {
                delete cleaned[key];
            }
        });

        return cleaned;
    }

    private markFormGroupTouched(): void {
        Object.keys(this.ruleForm.controls).forEach(key => {
            const control = this.ruleForm.get(key);
            control?.markAsTouched();

            if (control instanceof FormArray) {
                control.controls.forEach(arrayControl => {
                    if (arrayControl instanceof FormGroup) {
                        Object.keys(arrayControl.controls).forEach(arrayKey => {
                            arrayControl.get(arrayKey)?.markAsTouched();
                        });
                    }
                });
            }
        });
    }

    getRuleTypeDisplayName(): string {
        return RULE_TYPE_CONFIG[this.ruleType]?.displayName || 'Rules';
    }

    getDrainageVentilationSections(itemGroup: any): any[] {
        const sectionsArray = itemGroup.get('drainageVentilationSections') as FormArray;
        return sectionsArray ? sectionsArray.controls : [];
    }

    getJointSections(jointGroup?: any): any[] {
        return this.jointSectionsArray.controls;
    }
    
    getCrossSections(sectionGroup?: any): any[] {
        return this.crossSectionsArray.controls;
    }

     onTransformationUpdated(event: { crossSection: any; transformation: any }): void {
    const { crossSection, transformation } = event;
    
    // Update the cross section with new transformation data
    if (crossSection) {
      crossSection.transformation = {
        positionX: transformation.positionX,
        positionY: transformation.positionY,
        positionZ: transformation.positionZ,
        scaleX: transformation.scaleX,
        scaleY: transformation.scaleY,
        rotation: transformation.rotation,
        isMirrored: transformation.isMirrored
      };
    }
    
    // Mark form as dirty so save button enables
    this.ruleForm.markAsDirty();
  }
    getSystems(): void {
       const params: QueryParams = {
            page: 1
        };
        this.systemsService.getSystems(params).subscribe({
            next: (response: SystemModelPagedResponseModel | any) => {
                // Handle both camelCase (new) and PascalCase (old) formats
                const data = response.data || response.Data || [];
                console.log('Raw systems data:', data);
                // Transform systems from PascalCase to camelCase if needed
                this.systems = Array.isArray(data) 
                    ? data.map(system => this.transformSystemIfNeeded(system))
                    : [];
                    this.applySelectedSystem();
                console.log('Loaded systems:', this.systems);
               
            },
            error: (error) => {
                console.error('Error loading systems:', error);
                this.showError('Could not load systems.');
                this.loading = false;
            }
        });
    }

        // Transform system object from PascalCase to camelCase if needed
    private transformSystemIfNeeded(system: any): SystemModel {
        if (!system) return system;
        if (typeof system !== 'object') return system;
        
        // Check if already in camelCase (has lowercase properties)
        if (system.name !== undefined || system.id !== undefined) {
            // Already in camelCase, but check nested objects and return a new object to avoid mutation
            const transformed: SystemModel = {
                ...system,
                regions: system.regions && Array.isArray(system.regions)
                    ? system.regions.map((region: any) => this.transformRegionIfNeeded(region))
                    : system.regions,
                    matingRules: system.matingRules && Array.isArray(system.matingRules)
            ? system.matingRules.map((rule: any) => this.transformMatingRuleToCamelCase(rule))
            : system.matingRules,
            jointRules: system.jointRules  && Array.isArray(system.jointRules)
            ? system.jointRules.map((rule: any) => this.transformJointRuleToCamelCase(rule))
            : system.jointRules,
            
            drainageVentilationRules: system.drainageVentilationRules || [],
            limitationRules: system.limitationRules || [],
            additionalRules: system.additionalRules || [],
            integratedRules: system.integratedRules || [],
            glazingRules: system.glazingRules || [],
            holes: system.holes || [],
             id: system.Id || system.id,
            name: system.Name || system.name || '',
            version: system.Version || system.version,
            description: system.Description || system.description,
            material: system.Material || system.material,
            type: system.Type || system.type,
            articleIds: system.ArticleIds || system.articleIds,
            articles: system.Articles || system.articles,
            createdAt: system.CreatedAt || system.createdAt,
            updatedAt: system.UpdatedAt || system.updatedAt
            };
            return transformed;
        }
        
        // Transform from PascalCase to camelCase
        const transformed: SystemModel = {
            id: system.Id || system.id,
            name: system.Name || system.name || '',
            version: system.Version || system.version,
            description: system.Description || system.description,
            material: system.Material || system.material,
            type: system.Type || system.type,
            regions: (system.Regions || system.regions || []).map((region: any) => this.transformRegionIfNeeded(region)),
            articleIds: system.ArticleIds || system.articleIds,
            articles: system.Articles || system.articles,
            createdAt: system.CreatedAt || system.createdAt,
            updatedAt: system.UpdatedAt || system.updatedAt,
            matingRules: (system.matingRules || system.MatingRules).map((rule: any) => this.transformMatingRuleToCamelCase(rule)),
            jointRules: (system.jointRules || system.JointRules).map((rule: any) => this.transformJointRuleToCamelCase(rule)),
            drainageVentilationRules: system.drainageVentilationRules || [],
            limitationRules: system.limitationRules || [],
            additionalRules: system.additionalRules || [],
            integratedRules: system.integratedRules || [],
            glazingRules: system.glazingRules || [],
            holes: system.holes || []
        };
        
        return transformed;
    }

    // Transform region object from PascalCase to camelCase if needed
    private transformRegionIfNeeded(region: any): any {
        if (!region) return region;
        if (typeof region !== 'object') return region;
        
        // Check if already in camelCase
        if (region.country !== undefined || region.id !== undefined) {
            return region;
        }
        
        // Transform from PascalCase to camelCase
        return {
            id: region.Id || region.id,
            country: region.Country || region.country,
            city: region.City || region.city,
            state: region.State || region.state,
            district: region.District || region.district,
            place: region.Place || region.place
        };
    }

         private transformJointRuleToCamelCase(rule: any): JointRuleModel {
          console.log('Transforming rule to camelCase:', rule);
          return {
              id: rule.Id || rule.id,
              ruleId: rule.RuleId || rule.ruleId,
              name: rule.Name || rule.name,
              description: rule.Description || rule.description,
              systemId: rule.SystemId || rule.systemId,
  
              jointSections: (rule.JointSections || rule.jointSections || []).map((cs: any) => ({
                  id: cs.Id || cs.id,
                  description: cs.Description || cs.description,
                  material: cs.Material || cs.material,
                  type: cs.Type || cs.type,
                  imageUrl: cs.ImageUrl || cs.imageUrl,
                  articleIds: cs.ArticleIds || cs.articleIds,
                  articles: (cs.Articles || cs.articles || []).map((a: any) => this.transformArticle(a))
              })),
  
              createdAt: rule.CreatedAt || rule.createdAt,
              updatedAt: rule.UpdatedAt || rule.updatedAt
          };
      }

      // Transform article object from PascalCase to camelCase
      private transformArticle(article: any): any {
          if (!article) return article;
          if (typeof article !== 'object') return article;
  
          // Check if already in camelCase
          if (article.articleName !== undefined || article.id !== undefined) {
              return article;
          }
  
          // Transform from PascalCase
          return {
              id: article.Id || article.id,
              articleName: article.ArticleName || article.articleName,
              material: article.Material || article.material,
              type: article.Type || article.type,
              systemIds: article.SystemIds || article.systemIds,
              systems: (article.Systems || article.systems || []).map((system: any) => this.transformSystem(system)),
              createdAt: article.CreatedAt || article.createdAt,
              updatedAt: article.UpdatedAt || article.updatedAt
          };
      }

        // Transform system object from PascalCase to camelCase (if needed for nested systems)
      private transformSystem(system: any): any {
          if (!system) return system;
          if (typeof system !== 'object') return system;
  
          // Check if already in camelCase
          if (system.name !== undefined || system.id !== undefined) {
              return system;
          }
  
          // Basic transformation - only transform if needed
          return {
              id: system.Id || system.id,
              name: system.Name || system.name,
              material: system.Material || system.material,
              type: system.Type || system.type
          };
      }

          private transformMatingRuleToCamelCase(rule: any): MatingRuleModel {
              console.log('Transforming rule to camelCase:', rule);
              return {
                  id: rule.Id || rule.id,
                  ruleId: rule.RuleId || rule.ruleId,
                  name: rule.Name || rule.name,
                  description: rule.Description || rule.description,
                  systemId: rule.SystemId || rule.systemId,
      
                  crossSections: (rule.CrossSections || rule.crossSections || []).map((cs: any) => ({
                      id: cs.Id || cs.id,
                      description: cs.Description || cs.description,
                      material: cs.Material || cs.material,
                      type: cs.Type || cs.type,
                      imageUrl: cs.ImageUrl || cs.imageUrl,
                      transformation: this.transformTransformation(cs.Transformation || cs.transformation),
                      articleIds: cs.ArticleIds || cs.articleIds,
                      articles: (cs.Articles || cs.articles || []).map((a: any) => this.transformArticle(a))
                  })),
      
                  createdAt: rule.CreatedAt || rule.createdAt,
                  updatedAt: rule.UpdatedAt || rule.updatedAt
              };
          }
      
          // Transform transformation object from PascalCase to camelCase
          private transformTransformation(transformation: any): TransformationModel {
              console.log("Raw transformation from API:", JSON.stringify(transformation, null, 2));
      
              if (!transformation || typeof transformation !== "object") {
                  return {
                      positionX: 0,
                      positionY: 0,
                      positionZ: 0,
                      rotation: 0,
                      scaleX: 1,
                      scaleY: 1,
                      isMirrored: false
                  };
              }
      
              return {
                  positionX: transformation.PositionX ?? transformation.positionX ?? 0,
                  positionY: transformation.PositionY ?? transformation.positionY ?? 0,
                  positionZ: transformation.PositionZ ?? transformation.positionZ ?? 0,
                  rotation: transformation.Rotation ?? transformation.rotation ?? 0,
                  scaleX: transformation.ScaleX ?? transformation.scaleX ?? 1,
                  scaleY: transformation.ScaleY ?? transformation.scaleY ?? 1,
                  isMirrored:
                      transformation.IsMirrored ??
                      transformation.isMirrored ??
                      false
              };
          }
  
}
